import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings as SettingsIcon, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [settings, setSettings] = useState({
    whatsapp_number: '',
    razorpay_key_id: ''
  });
  const [form, setForm] = useState({
    whatsapp_number: '',
    razorpay_key_id: '',
    razorpay_key_secret: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setForm({
        whatsapp_number: response.data.whatsapp_number,
        razorpay_key_id: response.data.razorpay_key_id,
        razorpay_key_secret: ''
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, form);
      toast.success('Settings updated successfully! Please restart backend for payment changes.');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  return (
    <div className="settings-page min-h-screen bg-gradient-to-br from-[#f5f3f8] to-[#f5f3f8] py-20">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <SettingsIcon className="w-12 h-12 text-[#a855f7]" />
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-800" data-testid="settings-header">Settings</h1>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="settings-form">
              {/* WhatsApp Number */}
              <div className="space-y-2">
                <Label htmlFor="whatsapp_number" className="text-lg font-semibold text-gray-800">
                  WhatsApp Number
                </Label>
                <Input
                  data-testid="whatsapp-input"
                  id="whatsapp_number"
                  name="whatsapp_number"
                  type="text"
                  value={form.whatsapp_number}
                  onChange={handleChange}
                  placeholder="+919876543210"
                  className="text-lg p-6 rounded-xl"
                />
                <p className="text-sm text-gray-500">This number will appear on the WhatsApp click-to-chat button</p>
              </div>

              {/* Razorpay Key ID */}
              <div className="space-y-2">
                <Label htmlFor="razorpay_key_id" className="text-lg font-semibold text-gray-800">
                  Razorpay Key ID
                </Label>
                <Input
                  data-testid="razorpay-key-input"
                  id="razorpay_key_id"
                  name="razorpay_key_id"
                  type="text"
                  value={form.razorpay_key_id}
                  onChange={handleChange}
                  placeholder="rzp_test_xxxxxxxx"
                  className="text-lg p-6 rounded-xl"
                />
              </div>

              {/* Razorpay Secret Key */}
              <div className="space-y-2">
                <Label htmlFor="razorpay_key_secret" className="text-lg font-semibold text-gray-800">
                  Razorpay Secret Key
                </Label>
                <Input
                  data-testid="razorpay-secret-input"
                  id="razorpay_key_secret"
                  name="razorpay_key_secret"
                  type="password"
                  value={form.razorpay_key_secret}
                  onChange={handleChange}
                  placeholder="Enter secret key to update"
                  className="text-lg p-6 rounded-xl"
                />
                <p className="text-sm text-gray-500">Secret key is not displayed for security. Leave empty to keep current value.</p>
              </div>

              {/* Submit Button */}
              <Button 
                data-testid="save-settings-btn"
                type="submit"
                className="w-full bg-[#a855f7] hover:bg-[#9333ea] text-white py-6 text-lg rounded-full"
              >
                Save Settings <Save className="ml-2" />
              </Button>
            </form>

            {/* Help Text */}
            <div className="mt-8 p-6 bg-gradient-to-br from-[#f5f3f8] to-[#f5f3f8] rounded-xl">
              <h3 className="font-bold text-gray-800 mb-2">How to get Razorpay Keys:</h3>
              <ol className="list-decimal list-inside space-y-1 text-gray-700">
                <li>Sign up at razorpay.com</li>
                <li>Go to Settings → API Keys</li>
                <li>Generate Test/Live keys</li>
                <li>Copy and paste them here</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;